import { Component, Input, OnInit, OnChanges, SimpleChanges, ElementRef, Renderer2 as Renderer } from '@angular/core';
import { ColumnConfig, ColumnTypeEnum, ColorTypeEnum } from 'ngx-zcs-components';
import { BehaviorSubject } from 'rxjs';

import { SessionDetailsView } from '../../upgrade-logs-viewer.model';
import { UpgradeEvent } from 'ngx-core-services';
import { UpgradeLogsFilterService } from '../../services/upgrade-logs-filter.service';
import { CreateCustomBlockSizeType, ICustomBlock } from '../../../decorators';

@CreateCustomBlockSizeType({
  'breakpoint-768': 768,
})
@Component({
  selector: 'lib-session-details',
  templateUrl: './session-details.component.html',
  styleUrls: ['./session-details.component.scss']
})
export class SessionDetailsComponent implements OnInit, OnChanges, ICustomBlock {
  @Input() sessionDetails: SessionDetailsView | null = null;
  @Input() labels: any = {};

  selectedTabIndex = 0;
  selectedSnapshotView: 'pre' | 'post' | 'comparison' = 'pre';

  // Configuration for azzurro-table
  eventsPaginationConfig = {
    pageSize: 15,
    pageSizeOptions: [15, 25, 50],
    length: 0,
  };

  sgpCommandsPaginationConfig = {
    pageSize: 10,
    pageSizeOptions: [10, 25, 50],
    length: 0,
  };

  // Observable data sources for azzurro-table
  private eventsSubject = new BehaviorSubject<UpgradeEvent[]>([]);
  eventsData$ = this.eventsSubject.asObservable();

  private sgpCommandsSubject = new BehaviorSubject<UpgradeEvent[]>([]);
  sgpCommandsData$ = this.sgpCommandsSubject.asObservable();

  // ICustomBlock properties
  width?: number;
  currentWidth?: number;

  constructor(
    public filterService: UpgradeLogsFilterService,
    public el: ElementRef,
    public renderer: Renderer
  ) {}

  ngOnInit() {
    this.initializeColumns();
    this.updateData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sessionDetails']) {
      this.updateData();
    }
  }

  private updateData(): void {
    if (this.sessionDetails) {
      // Update events data
      this.eventsSubject.next(this.sessionDetails.events);
      this.eventsPaginationConfig = {
        ...this.eventsPaginationConfig,
        length: this.sessionDetails.events.length
      };

      // Update SGP commands data
      this.sgpCommandsSubject.next(this.sessionDetails.sgpCommands);
      this.sgpCommandsPaginationConfig = {
        ...this.sgpCommandsPaginationConfig,
        length: this.sessionDetails.sgpCommands.length
      };

      // Update computed data properties
      this.updateSummaryData();
    }
  }

  private updateSummaryData(): void {
    if (!this.sessionDetails) {
      this.summaryStats = [];
      this.sessionInfoData = [];
      this.basicDeviceSnapshotData = [];
      this.technicalDeviceSnapshotData = [];
      this.preUpgradeSnapshotData = [];
      this.preUpgradeSnapshotTechnicalData = [];
      this.postUpgradeSnapshotData = [];
      this.postUpgradeSnapshotTechnicalData = [];
      this.batteryModuleComparisonData = [];
      return;
    }

    // Update summary stats
    this.summaryStats = [
      { label: 'Total Events', value: this.sessionDetails.statistics.totalEvents.toString(), bgColor: 'bg-blue-200' },
      { label: 'SGP Commands', value: this.sessionDetails.statistics.totalSgpCommands.toString(), bgColor: 'bg-green-200' },
      { label: 'Errors', value: this.sessionDetails.statistics.totalErrors.toString(), bgColor: 'bg-red-200' },
      { label: 'Duration', value: this.sessionDuration, bgColor: 'bg-purple-200' }
    ];

    // Update session info data
    const info = [
      { label: 'Session ID', value: this.sessionDetails.session.sessionId, bgColor: 'bg-grey-200' },
      { label: 'Device Serial', value: this.sessionDetails.session.deviceSerial, bgColor: 'bg-ottanio-200' },
      { label: 'Device Model', value: this.sessionDetails.session.deviceModel, bgColor: 'bg-green-200' },
      { label: 'Operator', value: this.sessionDetails.session.operatorName, bgColor: 'bg-orange-200' },
      { label: 'Operator Email', value: this.sessionDetails.session.operatorEmail, bgColor: 'bg-yellow-200' },
      { label: 'Start Time', value: this.formatEventTimestamp(this.sessionDetails.session.startTime), bgColor: 'bg-accent-200' }
    ];

    // Add end time if available
    if (this.sessionDetails.session.endTime) {
      info.push({
        label: 'End Time',
        value: this.formatEventTimestamp(this.sessionDetails.session.endTime),
        bgColor: 'bg-purple-200'
      });
    }

    // Add firmware targets to session info (only current and target)
    if (this.sessionDetails.session.fwItemsTarget?.length) {
      this.sessionDetails.session.fwItemsTarget.forEach((target: any) => {
        if (target && typeof target === 'object') {
          info.push({
            label: `${target.name} Current`,
            value: target.current || 'unknown',
            bgColor: 'bg-blue-200'
          });
          info.push({
            label: `${target.name} Target`,
            value: target.latest || 'unknown',
            bgColor: 'bg-green-200'
          });
        }
      });
    }

    this.sessionInfoData = info;

    // Update legacy device snapshot data (for backwards compatibility)
    if (this.sessionDetails.deviceSnapshot) {
      const snapshot = this.sessionDetails.deviceSnapshot;
      this.basicDeviceSnapshotData = [
        { label: 'Captured At', value: this.formatEventTimestamp(snapshot.timestamp), bgColor: 'bg-grey-200' },
      ];

      this.technicalDeviceSnapshotData = [
        { label: 'Inverter Status', value: JSON.stringify(snapshot.inverterStatus, null, 2) },
        { label: 'Battery Details', value: JSON.stringify(snapshot.batteryDetails, null, 2) }
      ];
    } else {
      this.basicDeviceSnapshotData = [];
      this.technicalDeviceSnapshotData = [];
    }

    // Update pre-upgrade snapshot data
    this.updatePreUpgradeSnapshotData();

    // Update post-upgrade snapshot data
    this.updatePostUpgradeSnapshotData();

    // Update battery module comparison data
    this.updateBatteryModuleComparisonData();

  }

  // Column configurations for azzurro-table - using properties instead of getters to avoid infinite loops
  eventsColumns: ColumnConfig[] = [];
  sgpCommandsColumns: ColumnConfig[] = [];

  // Data properties - calculated once when data changes to avoid re-rendering
  summaryStats: Array<{label: string, value: string, bgColor?: string}> = [];
  sessionInfoData: Array<{label: string, value: string, bgColor?: string}> = [];
  basicDeviceSnapshotData: Array<{label: string, value: string, bgColor?: string}> = [];
  technicalDeviceSnapshotData: Array<{label: string, value: string}> = [];

  // Pre and post upgrade snapshot data
  preUpgradeSnapshotData: Array<{label: string, value: string, bgColor?: string}> = [];
  preUpgradeSnapshotTechnicalData: Array<{label: string, value: string}> = [];
  postUpgradeSnapshotData: Array<{label: string, value: string, bgColor?: string}> = [];
  postUpgradeSnapshotTechnicalData: Array<{label: string, value: string}> = [];
  batteryModuleComparisonData: Array<{label: string, preBatteryValue: string, postBatteryValue: string, hasChange: boolean}> = [];

  private initializeColumns(): void {
    this.eventsColumns = [
      {
        columnDef: 'timestamp',
        header: 'Timestamp',
        type: ColumnTypeEnum.Date,
        sortable: true,
        cell: (event: UpgradeEvent) => new Date(event.timestamp),
        format: 'dd/MM/yyyy HH:mm:ss'
      },
      {
        columnDef: 'operation',
        header: 'Operation',
        type: ColumnTypeEnum.Chip,
        sortable: true,
        cell: (event: UpgradeEvent) => event.operation,
        chipColor: () => ColorTypeEnum.Primary
      },
      {
        columnDef: 'phase',
        header: 'Phase',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (event: UpgradeEvent) => event.phase || 'N/A'
      },
      {
        columnDef: 'progress',
        header: 'Progress',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (event: UpgradeEvent) => this.formatProgress(event.progress)
      },
      {
        columnDef: 'moduleId',
        header: 'Module',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (event: UpgradeEvent) => event.moduleId || 'N/A'
      }
    ];

    this.sgpCommandsColumns = [
      {
        columnDef: 'timestamp',
        header: 'Timestamp',
        type: ColumnTypeEnum.Date,
        sortable: true,
        cell: (command: UpgradeEvent) => new Date(command.timestamp),
        format: 'dd/MM/yyyy HH:mm:ss'
      },
      {
        columnDef: 'commandCode',
        header: 'Command Code',
        type: ColumnTypeEnum.Text,
        sortable: true,
        cell: (command: UpgradeEvent) => this.formatCommandCode(command.sgpCommandDetails?.commandCode)
      },
      {
        columnDef: 'rawCommand',
        header: 'Raw Command',
        type: ColumnTypeEnum.Text,
        cell: (command: UpgradeEvent) => this.formatHexData(command.sgpCommandDetails?.rawCommand)
      },
      {
        columnDef: 'rawResponse',
        header: 'Raw Response',
        type: ColumnTypeEnum.Text,
        cell: (command: UpgradeEvent) => this.formatHexData(command.sgpCommandDetails?.rawResponse)
      }
    ];
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  get sessionDuration(): string {
    if (!this.sessionDetails?.session.endTime) return 'Ongoing';
    const duration = this.sessionDetails.session.endTime - this.sessionDetails.session.startTime;
    return this.filterService.formatDuration(duration);
  }

  private updatePreUpgradeSnapshotData(): void {
    const preSnapshot = this.sessionDetails?.session?.preUpgradeSnapshot;

    if (preSnapshot) {
      this.preUpgradeSnapshotData = [
        { label: 'Captured At', value: this.formatEventTimestamp(preSnapshot.timestamp), bgColor: 'bg-blue-200' },
        { label: 'Selected Modules', value: preSnapshot.selectedModules?.join(', ') || 'N/A', bgColor: 'bg-purple-200' }
      ];

      this.preUpgradeSnapshotTechnicalData = [
        { label: 'Inverter Status', value: JSON.stringify(preSnapshot.inverterStatus, null, 2) },
        { label: 'Battery Details', value: JSON.stringify(preSnapshot.batteryDetails, null, 2) }
      ];

      // Add battery module data if available
      if (preSnapshot.batteryModuleData) {
        if (preSnapshot.batteryModuleData.bdu) {
          this.preUpgradeSnapshotTechnicalData.push({
            label: 'BDU Module Details',
            value: JSON.stringify(preSnapshot.batteryModuleData.bdu, null, 2)
          });
        }
        if (preSnapshot.batteryModuleData.bms) {
          this.preUpgradeSnapshotTechnicalData.push({
            label: 'BMS Module Details',
            value: JSON.stringify(preSnapshot.batteryModuleData.bms.details, null, 2)
          });
          this.preUpgradeSnapshotTechnicalData.push({
            label: 'BMS Module Realtime',
            value: JSON.stringify(preSnapshot.batteryModuleData.bms.realtime, null, 2)
          });
        }
        if (preSnapshot.batteryModuleData.pcu) {
          this.preUpgradeSnapshotTechnicalData.push({
            label: 'PCU Module Details',
            value: JSON.stringify(preSnapshot.batteryModuleData.pcu.details, null, 2)
          });
          this.preUpgradeSnapshotTechnicalData.push({
            label: 'PCU Module Config',
            value: JSON.stringify(preSnapshot.batteryModuleData.pcu.config, null, 2)
          });
        }
      }
    } else {
      this.preUpgradeSnapshotData = [];
      this.preUpgradeSnapshotTechnicalData = [];
    }
  }

  private updatePostUpgradeSnapshotData(): void {
    const postSnapshot = this.sessionDetails?.session?.postUpgradeSnapshot;

    if (postSnapshot) {
      this.postUpgradeSnapshotData = [
        { label: 'Captured At', value: this.formatEventTimestamp(postSnapshot.timestamp), bgColor: 'bg-green-200' },
        { label: 'Selected Modules', value: postSnapshot.selectedModules?.join(', ') || 'N/A', bgColor: 'bg-purple-200' }
      ];

      this.postUpgradeSnapshotTechnicalData = [
        { label: 'Inverter Status', value: JSON.stringify(postSnapshot.inverterStatus, null, 2) },
        { label: 'Battery Details', value: JSON.stringify(postSnapshot.batteryDetails, null, 2) }
      ];

      // Add battery module data if available
      if (postSnapshot.batteryModuleData) {
        if (postSnapshot.batteryModuleData.bdu) {
          this.postUpgradeSnapshotTechnicalData.push({
            label: 'BDU Module Details',
            value: JSON.stringify(postSnapshot.batteryModuleData.bdu, null, 2)
          });
        }
        if (postSnapshot.batteryModuleData.bms) {
          this.postUpgradeSnapshotTechnicalData.push({
            label: 'BMS Module Details',
            value: JSON.stringify(postSnapshot.batteryModuleData.bms.details, null, 2)
          });
          this.postUpgradeSnapshotTechnicalData.push({
            label: 'BMS Module Realtime',
            value: JSON.stringify(postSnapshot.batteryModuleData.bms.realtime, null, 2)
          });
        }
        if (postSnapshot.batteryModuleData.pcu) {
          this.postUpgradeSnapshotTechnicalData.push({
            label: 'PCU Module Details',
            value: JSON.stringify(postSnapshot.batteryModuleData.pcu.details, null, 2)
          });
          this.postUpgradeSnapshotTechnicalData.push({
            label: 'PCU Module Config',
            value: JSON.stringify(postSnapshot.batteryModuleData.pcu.config, null, 2)
          });
        }
      }
    } else {
      this.postUpgradeSnapshotData = [];
      this.postUpgradeSnapshotTechnicalData = [];
    }
  }

  private updateBatteryModuleComparisonData(): void {
    const preSnapshot = this.sessionDetails?.session?.preUpgradeSnapshot;
    const postSnapshot = this.sessionDetails?.session?.postUpgradeSnapshot;

    if (!preSnapshot || !postSnapshot) {
      this.batteryModuleComparisonData = [];
      return;
    }

    const comparisonData: Array<{label: string, preBatteryValue: string, postBatteryValue: string, hasChange: boolean}> = [];

    // Compare inverter status
    const preInverterStr = JSON.stringify(preSnapshot.inverterStatus, null, 2);
    const postInverterStr = JSON.stringify(postSnapshot.inverterStatus, null, 2);
    comparisonData.push({
      label: 'Inverter Status',
      preBatteryValue: preInverterStr,
      postBatteryValue: postInverterStr,
      hasChange: preInverterStr !== postInverterStr
    });

    // Compare battery details
    const preBatteryStr = JSON.stringify(preSnapshot.batteryDetails, null, 2);
    const postBatteryStr = JSON.stringify(postSnapshot.batteryDetails, null, 2);
    comparisonData.push({
      label: 'Battery Details',
      preBatteryValue: preBatteryStr,
      postBatteryValue: postBatteryStr,
      hasChange: preBatteryStr !== postBatteryStr
    });

    // Compare battery module data if both have it
    if (preSnapshot.batteryModuleData && postSnapshot.batteryModuleData) {
      // BDU comparison
      if (preSnapshot.batteryModuleData.bdu && postSnapshot.batteryModuleData.bdu) {
        const preBduStr = JSON.stringify(preSnapshot.batteryModuleData.bdu, null, 2);
        const postBduStr = JSON.stringify(postSnapshot.batteryModuleData.bdu, null, 2);
        comparisonData.push({
          label: 'BDU Module',
          preBatteryValue: preBduStr,
          postBatteryValue: postBduStr,
          hasChange: preBduStr !== postBduStr
        });
      }

      // BMS comparison
      if (preSnapshot.batteryModuleData.bms && postSnapshot.batteryModuleData.bms) {
        const preBmsStr = JSON.stringify(preSnapshot.batteryModuleData.bms, null, 2);
        const postBmsStr = JSON.stringify(postSnapshot.batteryModuleData.bms, null, 2);
        comparisonData.push({
          label: 'BMS Module',
          preBatteryValue: preBmsStr,
          postBatteryValue: postBmsStr,
          hasChange: preBmsStr !== postBmsStr
        });
      }

      // PCU comparison
      if (preSnapshot.batteryModuleData.pcu && postSnapshot.batteryModuleData.pcu) {
        const prePcuStr = JSON.stringify(preSnapshot.batteryModuleData.pcu, null, 2);
        const postPcuStr = JSON.stringify(postSnapshot.batteryModuleData.pcu, null, 2);
        comparisonData.push({
          label: 'PCU Module',
          preBatteryValue: prePcuStr,
          postBatteryValue: postPcuStr,
          hasChange: prePcuStr !== postPcuStr
        });
      }
    }

    this.batteryModuleComparisonData = comparisonData;
  }

  get hasPreUpgradeSnapshot(): boolean {
    return !!this.sessionDetails?.session?.preUpgradeSnapshot;
  }

  get hasPostUpgradeSnapshot(): boolean {
    return !!this.sessionDetails?.session?.postUpgradeSnapshot;
  }

  get hasBothSnapshots(): boolean {
    return this.hasPreUpgradeSnapshot && this.hasPostUpgradeSnapshot;
  }

  // Events tab helpers
  formatEventTimestamp(timestamp: number): string {
    return this.filterService.formatTimestamp(timestamp);
  }

  formatProgress(progress?: number): string {
    if (progress === undefined || progress === null) return '--';
    return `${progress}%`;
  }

  // SGP Commands tab helpers
  formatHexData(hexData?: string): string {
    if (!hexData) return 'N/A';
    // Format hex data with spaces every 2 characters for better readability
    return hexData.replace(/(.{2})/g, '$1 ').trim();
  }

  formatCommandCode(commandCode?: string): string {
    return commandCode || 'Unknown';
  }

  // Export functionality
  exportSgpCommandsAsText(): void {
    if (!this.sessionDetails?.sgpCommands) return;

    const textContent = this.sessionDetails.sgpCommands.map(cmd => {
      const details = cmd.sgpCommandDetails;
      if (!details) return '';

      return `Command: ${details.commandCode}\n` +
             `Raw Command: ${details.rawCommand}\n` +
             `Raw Response: ${details.rawResponse || 'N/A'}\n` +
             `Success: ${details.success}\n` +
             `Timestamp: ${new Date(cmd.timestamp).toISOString()}\n` +
             `---\n`;
    }).join('\n');

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `sgp-commands-${this.sessionDetails.session.deviceSerial}-${this.sessionDetails.session.sessionId}.txt`;
    link.click();
    window.URL.revokeObjectURL(url);
  }
}
